import { type MouseEventHandler, useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { auth } from '@atlaskit/outbound-auth-flow-client/auth';
import { AuthError } from '@atlaskit/outbound-auth-flow-client/error';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type { EnhancedSmartLink } from '../utils/smartLinkTypes.ts';

const ANALYTICS_SOURCE = 'connectionsBylineMenu';

type ConnectAnalyticsStatus = 'success' | 'failed';

const getAnalyticsAttributes = (
	item: EnhancedSmartLink,
	status?: ConnectAnalyticsStatus,
	reason?: string | null,
) => ({
	product: item.product,
	resourceType: item.resourceType,
	authProviderKey: item.authProviders[0]?.key ?? null,
	access: item.access,
	occurrenceIndex: item.occurrenceIndex,
	...(status ? { status } : {}),
	...(reason !== undefined ? { reason } : {}),
});

const reportConnectError = (err: unknown) => {
	const errorObj = err instanceof Error ? err : new Error(String(err));
	getMonitoringClient().submitError(errorObj, {
		attribution: Attribution.INTEGRATIONS,
	});
};

export type UseConnectSmartLinkAccountResult = {
	connect: MouseEventHandler<HTMLButtonElement>;
	isConnecting: boolean;
};

type UseConnectSmartLinkAccountOptions = {
	item: EnhancedSmartLink;
	refreshLinkUrl: (item: EnhancedSmartLink) => Promise<EnhancedSmartLink | undefined>;
	onConnectSuccess?: () => void;
};

export const useConnectSmartLinkAccount = ({
	item,
	refreshLinkUrl,
	onConnectSuccess,
}: UseConnectSmartLinkAccountOptions): UseConnectSmartLinkAccountResult => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isConnecting, setIsConnecting] = useState(false);

	const connect = useCallback<MouseEventHandler<HTMLButtonElement>>(
		(event) => {
			event.stopPropagation();

			if (isConnecting) {
				return;
			}

			const authProvider = item.authProviders[0];
			if (!authProvider) {
				return;
			}

			setIsConnecting(true);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'connectionsBylineConnectButton',
					source: ANALYTICS_SOURCE,
					attributes: getAnalyticsAttributes(item),
				},
			}).fire();

			void (async () => {
				try {
					await auth(authProvider.url);
				} catch (err: unknown) {
					const reason = err instanceof AuthError ? (err.type ?? null) : null;
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'failed',
							actionSubject: 'auth',
							actionSubjectId: 'connectionsBylineConnectAuth',
							source: ANALYTICS_SOURCE,
							attributes: getAnalyticsAttributes(item, 'failed', reason),
						},
					}).fire();
					reportConnectError(err);
					setIsConnecting(false);
					return;
				}

				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						action: 'succeeded',
						actionSubject: 'auth',
						actionSubjectId: 'connectionsBylineConnectAuth',
						source: ANALYTICS_SOURCE,
						attributes: getAnalyticsAttributes(item, 'success'),
					},
				}).fire();

				try {
					await refreshLinkUrl(item);
					onConnectSuccess?.();
				} catch (err: unknown) {
					reportConnectError(err);
					setIsConnecting(false);
				}
			})();
		},
		[createAnalyticsEvent, isConnecting, item, onConnectSuccess, refreshLinkUrl],
	);

	return {
		connect,
		isConnecting,
	};
};
